import { Injectable, Injector } from '@angular/core';
import { BaseModel, EntityType } from '@cl/shared/models';
import { BaseService } from './base.service';
import { ClientsService } from './clients.service';
import { ContactsService } from './contacts.service';
import { CompaniesService } from './companies.service';
import { OrdersTypesService } from './orders-types.service';
import { WorkTypesService } from './work-types.service';
import { DepartmentsService } from './departments.service';
import { PayoutTypesService } from './payout-types.service';
import { PerformersService } from './performers.service';
import { UsersService } from './users.service';
import { ContractsService } from './contracts.service';
import { ScontractsService } from './scontracts.service';
import { WorksService } from './works.service';
import { CrmEventsService } from './crm-events.service';
import { FuelReportsService } from './fuel-reports.service';
import { PayoutsService } from './payouts.service';
import { DocumentsService } from './documents.service';
import { ScontractOrdersTypesService } from './scontract-orders-types.service';
import { EstimateService } from './estimate.service';
import { DocumentFilesService } from './document-files.service';
import { ComparisonActsService } from './comparison-acts.service';
import { WorkTimesService } from './work-times.service';
import { WebUsersService } from './web-users.service';
import { EstimatePositionTypesService } from './estimate-position-types.service';
import { BanksService } from './banks.service';
import { BaseLayersService } from './base-layers.service';
import { PrintoutTemplateService } from './printout-template.service';
import { WebTermsConditionsService } from './web-terms-conditions.service';
import { MotdMessagesService } from './motd-messages.service';
import { ClassifierService } from './classifier.service';
import { InvoicesService } from './invoices.service';
import { OrdersService } from './orders.service';
import { SinvoicesService } from './sinvoices.service';
import { ExpenseTypesService } from './expense-types.service';
import { ExpensesService } from './expenses.service';
import { ExpenseRequestTypesService } from './expense-request-types.service';
import { ExpenseRequestsService } from './expense-requests.service';
import { AbsenceTypesService } from './absence-types.service';
import { HelpMaterialsService } from './help-materials.service';
import { RouteTypesService } from './route-types.service';
import { AbsencesService } from './absences.service';
import { RoutesService } from './routes.service';
import { FuelReceiptsService } from './fuel-receipts.service';
import { ProductReceiptsService } from './product-receipts.service';
import { CarsService } from './cars.service';
import { CardsService } from './cards.service';
import { DocumentTypesService } from './document-types.service';
import { HrOrdersService } from '@cl/pages/hr-orders/services/hr-orders.service';

@Injectable({ providedIn: 'root' })
export class ServiceFactory {
    private serviceMap: { type: EntityType; serviceType: any }[] = [
        { type: EntityType.Client, serviceType: ClientsService },
        { type: EntityType.Contact, serviceType: ContactsService },
        { type: EntityType.Company, serviceType: CompaniesService },
        { type: EntityType.OrderType, serviceType: OrdersTypesService },
        { type: EntityType.WorkType, serviceType: WorkTypesService },
        { type: EntityType.Department, serviceType: DepartmentsService },
        { type: EntityType.PayoutType, serviceType: PayoutTypesService },
        { type: EntityType.Performer, serviceType: PerformersService },
        { type: EntityType.User, serviceType: UsersService },
        { type: EntityType.Contract, serviceType: ContractsService },
        { type: EntityType.Scontract, serviceType: ScontractsService },
        { type: EntityType.Work, serviceType: WorksService },
        { type: EntityType.CrmEvent, serviceType: CrmEventsService },
        { type: EntityType.FuelReport, serviceType: FuelReportsService },
        { type: EntityType.Payout, serviceType: PayoutsService },
        { type: EntityType.Document, serviceType: DocumentsService },
        { type: EntityType.ScontractOrderType, serviceType: ScontractOrdersTypesService },
        { type: EntityType.Estimate, serviceType: EstimateService },
        { type: EntityType.DocumentFile, serviceType: DocumentFilesService },
        { type: EntityType.ComparisonAct, serviceType: ComparisonActsService },
        { type: EntityType.WorkTime, serviceType: WorkTimesService },
        { type: EntityType.WebUser, serviceType: WebUsersService },
        { type: EntityType.EstimatePositionType, serviceType: EstimatePositionTypesService },
        { type: EntityType.Bank, serviceType: BanksService },
        { type: EntityType.BaseLayer, serviceType: BaseLayersService },
        { type: EntityType.PrintoutTemplate, serviceType: PrintoutTemplateService },
        { type: EntityType.WebTermsConditions, serviceType: WebTermsConditionsService },
        { type: EntityType.MotdMessage, serviceType: MotdMessagesService },
        { type: EntityType.Classifier, serviceType: ClassifierService },
        { type: EntityType.Invoice, serviceType: InvoicesService },
        { type: EntityType.Order, serviceType: OrdersService },
        { type: EntityType.Sinvoice, serviceType: SinvoicesService },
        { type: EntityType.ExpenseType, serviceType: ExpenseTypesService },
        { type: EntityType.Expense, serviceType: ExpensesService },
        { type: EntityType.ExpenseRequestType, serviceType: ExpenseRequestTypesService },
        { type: EntityType.ExpenseRequest, serviceType: ExpenseRequestsService },
        { type: EntityType.AbsenceType, serviceType: AbsenceTypesService },
        { type: EntityType.HelpMaterial, serviceType: HelpMaterialsService },
        { type: EntityType.Absence, serviceType: AbsencesService },
        { type: EntityType.RouteType, serviceType: RouteTypesService },
        { type: EntityType.Route, serviceType: RoutesService },
        { type: EntityType.FuelReceipt, serviceType: FuelReceiptsService },
        { type: EntityType.ProductReceipt, serviceType: ProductReceiptsService },
        { type: EntityType.Car, serviceType: CarsService },
        { type: EntityType.Card, serviceType: CardsService },
        { type: EntityType.DocumentType, serviceType: DocumentTypesService },
        { type: EntityType.HrOrder, serviceType: HrOrdersService },
    ];

    constructor(private injector: Injector) {}

    getService<T extends BaseModel>(modelType: EntityType): BaseService<T> {
        const serviceType = this.serviceMap.find((sm) => sm.type === modelType);
        if (!serviceType) {
            throw new Error(`Service for ModelType ${modelType} was not found`);
        }
        return this.injector.get<BaseService<T>>(serviceType.serviceType);
    }
}
